/**
 * App shell — JuiceProvider + IdleAttractorScope("app") + AtmosphereShell +
 * AppChrome, with the wouter Switch inside. The shell (chrome + stadium
 * atmosphere CSS) paints instantly from the service worker; routes arrive as
 * choreography (KICKOFF §3.3).
 *
 * AppChrome (24 Jul, real-device review) replaces the old always-on
 * BroadcastChrome prize banner — that component was Landing's own "GAMES
 * NIGHT · LIVE" treatment mounted globally by mistake (Landing has always had
 * its own inline eyebrow; the LIVE-bug/prize duo never belonged in the
 * shell). AppChrome is the actual persistent chrome the comp specifies:
 * hamburger → drawer (Locker Room, Trophy Room, Match Centre, Night Room,
 * Help, VAR, Terms) + a bell wired to the real inbox count. It self-gates on
 * session + route (renders nothing pre-auth or on full-bleed screens).
 *
 * screen_viewed fires on every route change; session_started once per boot
 * (lib/platform). OfflineBanner rides navigator.onLine.
 */
import { Suspense, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import { AtmosphereShell, OfflineBanner, Skeleton } from "@matchday/ui";
import { IdleAttractorScope, JuiceProvider, juice } from "@matchday/juice";
import Landing from "./routes/Landing";
import { LAZY_ROUTES } from "./routes";
import { AppChrome } from "./components/chrome/AppChrome";
import { emitSessionStarted, trackScreen, tracker } from "./lib/platform";

function useOnline(): { online: boolean; queued: number } {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [queued, setQueued] = useState(() => tracker.pending());

  useEffect(() => {
    const sync = () => {
      setOnline(navigator.onLine);
      setQueued(tracker.pending());
    };
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  return { online, queued };
}

export default function App() {
  const [location] = useLocation();
  const { online, queued } = useOnline();

  // Once per boot, with source derived from the ENTRY url (deeplink/share/organic).
  useEffect(() => {
    void emitSessionStarted(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every route change is a screen view.
  useEffect(() => {
    trackScreen(location);
  }, [location]);

  return (
    <JuiceProvider service={juice}>
      <IdleAttractorScope name="app">
        <AtmosphereShell>
          <OfflineBanner visible={!online} queuedCount={queued} />
          <AppChrome />
          <Suspense
            fallback={
              <div className="pl-route-fallback">
                <Skeleton variant="panel" clip="bevel" height={220} />
              </div>
            }
          >
            <Switch>
              <Route path="/" component={Landing} />
              {LAZY_ROUTES.map(({ path, component }) => (
                <Route key={path} path={path} component={component} />
              ))}
              <Route>
                {/* Unknown routes fall back to the landing surface — a magic
                    link with a mangled path must never dead-end (MATCH POINT). */}
                <Landing />
              </Route>
            </Switch>
          </Suspense>
        </AtmosphereShell>
      </IdleAttractorScope>
    </JuiceProvider>
  );
}
